import type { EngineEvent } from '@adiox/api';

export type Severity = 'info' | 'success' | 'warning' | 'danger';

/** The sound of an event, like MT5's per-event sounds; without one, the severity's own. */
export type SoundName = Severity | 'open' | 'profit' | 'loss' | 'stop' | 'order' | 'alert' | 'message';

export interface NotificationDraft {
  severity: Severity;
  title: string;
  text: string;
  sound?: SoundName;
}

/** Which sound an engine event makes (null: the severity's). */
export function soundOf(event: EngineEvent): SoundName | null {
  switch (event.type) {
    case 'deal': {
      const d = event.deal;
      if (d.type !== 'Buy' && d.type !== 'Sell') return null;
      if (d.entry === 'In') return 'open';
      if (d.reason === 'StopLoss') return 'stop';
      if (d.reason === 'StopOut') return 'danger';
      return d.profit + d.commission + d.swap > 0 ? 'profit' : 'loss';
    }
    case 'order':
      return event.order.status === 'Placed' || event.order.status === 'Canceled' ? 'order' : null;
    case 'alert_triggered':
      return 'alert';
    default:
      return null;
  }
}

/** A mark and a word for each severity, so meaning never rests on colour alone. */
export const SEVERITY_MARK: Record<Severity, { icon: string; label: string }> = {
  info: { icon: 'ℹ', label: 'Info' },
  success: { icon: '✓', label: 'Done' },
  warning: { icon: '⚠', label: 'Warning' },
  danger: { icon: '✕', label: 'Alert' },
};

export type DigitsOf = (symbol: string) => number;

const money = (v: number) => `${v > 0 ? '+' : ''}${v.toFixed(2)}`;

/**
 * Turns an engine event into something worth telling the user, or null when it is not (a market order's own order
 * record, position updates, account snapshots: the deal says it all).
 */
export function describeEvent(event: EngineEvent, digitsOf: DigitsOf = () => 5): NotificationDraft | null {
  switch (event.type) {
    case 'deal': {
      const d = event.deal;
      if (d.type === 'Balance' || d.type === 'Credit') {
        return {
          severity: 'info',
          title: d.type === 'Balance' ? 'Balance change' : 'Credit change',
          text: `${money(d.profit)}${d.comment ? ` (${d.comment})` : ''}`,
        };
      }
      const price = d.price.toFixed(digitsOf(d.symbol));
      const what = `${d.type.toLowerCase()} ${d.volume} ${d.symbol} at ${price}`;
      if (d.entry === 'In') {
        return { severity: 'info', title: 'Position opened', text: `#${d.positionId}: ${what}` };
      }
      const result = `profit ${money(d.profit + d.commission + d.swap)}`;
      switch (d.reason) {
        case 'StopLoss':
          return {
            severity: 'warning',
            title: 'Stop loss hit',
            text: `#${d.positionId} ${d.symbol} closed at ${price}, ${result}`,
          };
        case 'TakeProfit':
          return {
            severity: 'success',
            title: 'Take profit hit',
            text: `#${d.positionId} ${d.symbol} closed at ${price}, ${result}`,
          };
        case 'StopOut':
          return {
            severity: 'danger',
            title: 'Closed by stop out',
            text: `#${d.positionId} ${d.symbol} closed at ${price}, ${result}`,
          };
        case 'Expiration':
        case 'Rollover':
        case 'Manager':
          return {
            severity: 'info',
            title: `Position closed (${d.reason.toLowerCase()})`,
            text: `#${d.positionId} ${d.symbol} at ${price}, ${result}`,
          };
        default:
          return {
            severity: d.profit + d.commission + d.swap >= 0 ? 'success' : 'info',
            title: 'Position closed',
            text: `#${d.positionId} ${d.symbol} at ${price}, ${result}`,
          };
      }
    }
    case 'order': {
      const o = event.order;
      if (o.type === 'Buy' || o.type === 'Sell') return null; // a market order: its deal is reported
      const kind = o.type.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
      const at = o.priceOrder.toFixed(digitsOf(o.symbol));
      switch (o.status) {
        case 'Placed':
          return {
            severity: 'info',
            title: 'Pending order placed',
            text: `#${o.ticket}: ${kind} ${o.volumeCurrent} ${o.symbol} at ${at}`,
          };
        case 'Filled':
          return {
            severity: 'info',
            title: 'Pending order triggered',
            text: `#${o.ticket}: ${kind} ${o.symbol} at ${at}`,
          };
        case 'Canceled':
          return {
            severity: 'info',
            title: 'Pending order cancelled',
            text: `#${o.ticket}: ${kind} ${o.symbol}`,
          };
        case 'Expired':
          return {
            severity: 'warning',
            title: 'Pending order expired',
            text: `#${o.ticket}: ${kind} ${o.symbol}`,
          };
        case 'Rejected':
          return { severity: 'warning', title: 'Order rejected', text: `#${o.ticket}: ${kind} ${o.symbol}` };
      }
      return null;
    }
    case 'alert_triggered': {
      const a = event.alert;
      const field = a.field === 'Ask' ? 'ask' : 'bid';
      const verb = a.direction === 'Below' ? 'falls to' : 'rises to';
      return {
        severity: 'warning',
        title: `Price alert: ${a.symbol}`,
        text: `${field === 'ask' ? 'Ask' : 'Bid'} ${verb} ${a.price.toFixed(digitsOf(a.symbol))}: the ${field} is ${event.value.toFixed(digitsOf(a.symbol))}.`,
      };
    }
    case 'margin_call':
      return {
        severity: 'warning',
        title: 'Margin call',
        text: `Margin level ${event.marginLevel.toFixed(2)}%. Add funds or close positions to avoid a stop out.`,
      };
    case 'stop_out':
      return {
        severity: 'danger',
        title: 'Stop out',
        text: `Margin level ${event.marginLevel.toFixed(2)}%: position #${event.positionTicket} is being closed.`,
      };
    case 'challenge_warning': {
      const what = event.rule === 'DailyLoss' ? 'daily loss' : 'maximum loss';
      return {
        severity: event.level >= 90 ? 'danger' : 'warning',
        title: `Challenge: ${event.level}% of the ${what} limit used`,
        text: `${event.used.toFixed(2)} of ${event.limit.toFixed(2)} used. Breaking the limit closes your positions and ends the stage.`,
      };
    }
    case 'challenge': {
      // Every change to a challenge is an event; only the moment it passes or fails is worth telling.
      const c = event.challenge;
      const ended = c.endedAt ? Date.parse(c.endedAt) : NaN;
      if (Number.isNaN(ended) || Math.abs(Date.parse(event.time) - ended) > 5_000) return null;
      if (c.status === 'Failed') {
        return {
          severity: 'danger',
          title: 'Challenge failed',
          text: c.breachText ?? 'A rule was broken. Positions were closed and trading is off.',
        };
      }
      if (c.status === 'Passed') {
        return {
          severity: 'success',
          title: 'Stage passed',
          text: 'Your profit target is met. Your account manager will open the next stage.',
        };
      }
      return null;
    }
    default:
      return null;
  }
}

export interface ConnectionView {
  hubState: string;
  feedUp: boolean | null;
}

/**
 * What a change in the connection or the price feed means to the user. `wasLive` says whether the terminal had been fully
 * live before (so the first connection after opening the page is not announced).
 */
export function describeTransition(
  previous: ConnectionView,
  next: ConnectionView,
  wasLive: boolean,
): NotificationDraft | null {
  if (wasLive && previous.hubState === 'connected' && next.hubState !== 'connected') {
    return {
      severity: 'warning',
      title: 'Connection lost',
      text: 'Trying to reconnect. Prices and trading are paused until it is back.',
    };
  }
  if (wasLive && previous.hubState !== 'connected' && next.hubState === 'connected') {
    return {
      severity: 'success',
      title: 'Connection restored',
      text: 'Your positions and orders were re-checked with the server.',
    };
  }
  if (next.hubState === 'connected' && previous.feedUp !== false && next.feedUp === false) {
    return {
      severity: 'warning',
      title: 'Price feed down',
      text: 'Prices are stale and trading is paused until the feed returns.',
    };
  }
  if (next.hubState === 'connected' && previous.feedUp === false && next.feedUp === true) {
    return { severity: 'success', title: 'Price feed restored', text: 'Prices are live again.' };
  }
  return null;
}
